/*
  EditorView
  The form and document editor where forms are created and documents are mapped
*/

import { ActivatedRoute, Router } from '@angular/router';
import { Component, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Control, FormLayoutService } from '@app/state/form-layout';
import { ID } from '@datorama/akita';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Md5 } from 'ts-md5/dist/md5';
import { switchMap, tap } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';

import { FormRepository, FormService } from '@state/form';
import { FormWizardQuery, FormWizardStore } from '@state/form-wizard';
import { PusherService } from '@state/pusher';
import { RenderedFormService } from '@app/shared/components/rendered-form/rendered-form.service';
import { ZendeskService } from '@state/zendesk';

import { TestDataModalComponent } from './test-data-modal';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-preview-form',
  styleUrls: ['./preview-form.component.scss'],
  templateUrl: './preview-form.component.html'
})
export class PreviewFormComponent implements OnInit, OnDestroy {
  activeSection$ = this.formWizardQuery.select('activeSection');
  activeIndex$ = this.formWizardQuery.select('activeIndex');
  form$ = this.formRepository.previewForm$;

  controls: Control[] = [];
  workspaceContext: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private formLayoutService: FormLayoutService,
    private formRepository: FormRepository,
    private formService: FormService,
    private formWizardQuery: FormWizardQuery,
    private formWizardStore: FormWizardStore,
    private ngZone: NgZone,
    private pusherService: PusherService,
    private router: Router,
    private snackBar: MatSnackBar,
    private titleService: Title,
    private zendeskService: ZendeskService,
    public renderedFormService: RenderedFormService
  ) {}

  ngOnInit() {
    const { params } = this.activatedRoute.snapshot;
    const id: ID = params.id;
    const routeParent = this.router.url.split('/')[1];
    let testDataControl: Control = {
      click: () => {
        this.dialog
          .open(TestDataModalComponent, {
            data: {
              dynamicModels: this.renderedFormService.dynamicModels
            }
          })
          .afterClosed()
          .subscribe({
            next: () => {
              // Update preview form using change detection
              this.updatePreview(id);
            }
          });
      },
      label: 'Test Data'
    };
    this.controls.push(testDataControl);
    this.workspaceContext = `${routeParent}Workspace`;
    this.getForm(id);

    this.ngZone.runOutsideAngular(() => {
      this.zendeskService.toggleZendeskWidgetDisplay();
    });
  }

  ngOnDestroy() {
    this.zendeskService.toggleZendeskWidgetDisplay();
  }

  onBack(event: any): void {
    let i = this.formWizardStore.getValue().activeIndex.position - 1;
    this.formWizardStore.update({
      activeIndex: { position: i },
      activeSection: this.renderedFormService.visibleSections[i]
    });
    event.resetElements();
  }

  onNext(event: any): void {
    let i = this.formWizardStore.getValue().activeIndex.position + 1;
    this.formWizardStore.update({
      activeIndex: { position: i },
      activeSection: this.renderedFormService.visibleSections[i]
    });
    this.renderedFormService.isSubmitting = false;
    event.currentSection.resetElements();
  }

  bindPusherChannel(): void {
    let formId = this.formRepository.getPreviewFormId();
    let channelId: string = Md5.hashStr(`forms/${formId}`);

    let channel = this.pusherService.pusher.subscribe(channelId);
    channel.bind('form_update', () => {
      let snackBarRef = this.snackBar.open(
        'Updates to this form have been made. Please refresh this page to get latest',
        'Reload',
        {
          duration: 259200,
          horizontalPosition: 'center'
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      snackBarRef
        .onAction()
        .pipe(
          switchMap(() => this.formService.getAndStorePreviewForm$(formId)),
          tap({
            next: (form) => {
              let activeIndex = this.formWizardStore.getValue().activeIndex;
              this.formWizardStore.update({
                activeIndex,
                activeSection: form.sections[activeIndex.position]
              });
            }
          })
        )
        .subscribe();
    });
  }

  getForm(id: ID): void {
    this.formService
      .getAndStorePreviewForm$(id)
      .pipe(
        tap({
          next: (form) => {
            this.formWizardStore.update({ activeIndex: { position: 0 }, activeSection: form.sections[0] });

            this.bindPusherChannel();

            if (form.printable) {
              this.controls.push({
                click: () => window.print(),
                label: 'Print'
              });
            }

            this.renderedFormService.form = form;
            let title = form.title;
            this.titleService.setTitle(`Previewing "${title}" | Form Builder`);
            this.formLayoutService.setAllAttributes({
              controls: this.controls,
              title: `Previewing ${title}`
            });
          }
        })
      )
      .subscribe();
  }

  onSubmit(): void {
    if (this.renderedFormService.dynamicModels) {
      this.renderedFormService.isSubmitting = false;
      confirm('You have reached the end of the form');
    }
  }

  updatePreview(id: ID): void {
    this.controls = this.controls.filter((control: Control) => control.label !== 'Print');
    this.getForm(id);
  }
}
